import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { useNTBArticle } from '@/hooks/news/useNTBNews';
import { Loader2, ExternalLink, Check, X, CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface NTBIntegrationProps {
  onArticleSelect: (article: {
    title: string;
    context: string;
    contextUrl: string;
    contextImageUrl?: string;
    contextImageCaption?: string;
    articleData: any;
  }) => void;
  className?: string;
}

const NTBIntegration: React.FC<NTBIntegrationProps> = ({ 
  onArticleSelect, 
  className = "" 
}) => {
  const [articleId, setArticleId] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [calendarDate, setCalendarDate] = useState<Date | undefined>(undefined);
  const [enableFetch, setEnableFetch] = useState(false);
  const [showArticleDialog, setShowArticleDialog] = useState(false);
  const [lastRequestedId, setLastRequestedId] = useState('');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  
  const { data: articleResponse, isLoading, error } = useNTBArticle(articleId, {
    enabled: enableFetch && !!articleId,
    searchDate: selectedDate
  });

  const article = articleResponse?.article;
  const articleError = articleResponse?.error;
  const notFound = articleResponse?.notFound;

  // Debug logging
  React.useEffect(() => {
    if (articleResponse) {
      console.log('NTB article response:', articleResponse);
    }
    if (error) {
      console.error('NTB fetch error:', error);
    }
  }, [articleResponse, error]);

  // Notify when no article is found
  React.useEffect(() => {
    if (enableFetch && !isLoading && (notFound || articleError)) {
      toast.error(articleError || `Fant ingen NTB-artikkel for ID ${articleId}`);
      setEnableFetch(false);
    }
  }, [enableFetch, isLoading, notFound, articleError, articleId]);
  const handleFetchArticle = (e?: React.MouseEvent | React.KeyboardEvent) => {
    // Keep it form-safe
    // @ts-ignore
    e?.preventDefault?.();
    // @ts-ignore
    e?.stopPropagation?.();

    if (!selectedDate) {
      toast.error('Velg en dato for søket først.');
      return;
    }

    const raw = articleId.trim();
    const isNumeric = /^\d+$/.test(raw);
    const isNtbId = /^NTB[A-Z0-9-]+$/i.test(raw);
    if (!isNumeric && !isNtbId) {
      toast.error('Ugyldig ID. Bruk tall (f.eks. 19371332) eller NTB-ID (f.eks. NTBxxxx-...).');
      return;
    }
    const normalized = isNtbId ? raw.toUpperCase() : raw;
    if (normalized !== articleId) setArticleId(normalized);
    setLastRequestedId(normalized);
    console.log(`Fetching NTB article with ID: ${normalized} for date: ${selectedDate}`);
    setEnableFetch(true);
  };
  const handleUseArticle = () => {
    if (article) {
      // Prefer imageUrl/imageCaption from edge function; fallback to mediaList
      const getImageFromArticle = (a: any) => {
        const directUrl = a?.imageUrl || '';
        const directCaption = a?.imageCaption || '';
        if (directUrl) return { imageUrl: directUrl, imageCaption: directCaption };

        const mediaList = a?.mediaList || a?.media || a?.media_list;
        if (Array.isArray(mediaList) && mediaList.length) {
          const preferred = mediaList.find((m: any) => /(image|photo|picture)/i.test(m?.type || m?.contentType || m?.mediaType || '')) || mediaList[0];
          const url = preferred?.url || preferred?.href || preferred?.mediaUrl || preferred?.imageUrl || '';
          const caption = preferred?.caption || preferred?.description || preferred?.credit || a?.title || '';
          return { imageUrl: url || '', imageCaption: caption || '' };
        }
        return { imageUrl: '', imageCaption: '' };
      };

      const { imageUrl, imageCaption } = getImageFromArticle(article as any);

      console.log('NTB article image data (with fallback):', { imageUrl, imageCaption });

      onArticleSelect({
        title: article.title,
        context: JSON.stringify(article),
        contextUrl: `ntb:${lastRequestedId || articleId}`,
        contextImageUrl: imageUrl,
        contextImageCaption: imageCaption,
        articleData: article
      });
      toast.success('NTB-artikkel hentet og lagt til kontekst');
      setEnableFetch(false);
    }
  };
  const handleClear = () => {
    setArticleId('');
    setEnableFetch(false);
  };

  return (
    <Card className={className}>
        <CardHeader>
          <CardTitle className="text-sm font-medium">
            NTB Artikkel-integrasjon
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input
                placeholder="dd.mm.yyyy"
                value={selectedDate}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  setSelectedDate(inputValue);
                  
                  // Try to parse manual input
                  if (inputValue.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                    const [day, month, year] = inputValue.split('.').map(Number);
                    const date = new Date(year, month - 1, day);
                    if (date.getDate() === day && date.getMonth() === month - 1 && date.getFullYear() === year) {
                      setCalendarDate(date);
                    }
                  }
                }}
                className="flex-1 rounded-r-none"
              />
              <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="rounded-l-none border-l-0"
                    type="button"
                  >
                    <CalendarIcon className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={calendarDate}
                    onSelect={(date) => {
                      if (date) {
                        setCalendarDate(date);
                        setSelectedDate(format(date, 'dd.MM.yyyy'));
                        setIsCalendarOpen(false);
                      }
                    }}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            <div className="flex gap-2">
              <Input
                placeholder="Skriv inn NTB-ID eller dokument-ID (f.eks. NTBxxxx-... eller 19371332)"
                value={articleId}
                onChange={(e) => setArticleId(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    handleFetchArticle(e);
                  }
                }}
              />
              <Button 
                type="button"
                formNoValidate
                onClick={(e) => { 
                  e.preventDefault(); 
                  e.stopPropagation(); 
                  handleFetchArticle(e); 
                }}
                disabled={!articleId.trim() || !selectedDate || isLoading}
                size="sm"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Hent'
                )}
              </Button>
            </div>
          </div>

        {(error || articleError) && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3">
            <p className="text-destructive text-sm font-medium">Feil ved henting av artikkel:</p>
            <p className="text-destructive text-sm mt-1">{articleError || (error as any)?.message || 'Ukjent feil oppstod'}</p>
          </div>
        )}

        {!isLoading && enableFetch && !error && !articleError && !article && articleId && (
          <div className="bg-muted border border-border rounded-md p-3">
            <p className="text-muted-foreground text-sm font-medium">Ingen artikkel funnet</p>
            <p className="text-muted-foreground text-sm mt-1">Fant ingen NTB-artikkel for ID {articleId}. Kontroller at ID-en er korrekt.</p>
          </div>
        )}

        {enableFetch && article && (
          <div className="space-y-3 border-t pt-3">
            <div className="text-sm space-y-2">
              <div>
                <span className="font-medium">Tittel:</span>{' '}
                <button
                  onClick={() => setShowArticleDialog(true)}
                  className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                >
                  {article.title}
                </button>
              </div>
              {article.publishedDate && (
                <div className="text-xs text-gray-500">
                  Publisert: {new Date(article.publishedDate).toLocaleDateString('nb-NO')}
                </div>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button 
                type="button"
                onClick={handleUseArticle}
                size="sm"
                className="flex-1"
              >
                <Check className="h-4 w-4 mr-1" />
                Bruk denne artikkelen
              </Button>
              <Button 
                type="button"
                onClick={handleClear}
                variant="outline"
                size="sm"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Article Dialog */}
        <Dialog open={showArticleDialog} onOpenChange={setShowArticleDialog}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">
                {article?.title}
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              {article?.publishedDate && (
                <div className="text-sm text-gray-500 mb-4">
                  Publisert: {new Date(article.publishedDate).toLocaleDateString('nb-NO')}
                </div>
              )}
              <div 
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: article?.content || '' }}
              />
            </div>
          </DialogContent>
        </Dialog>

        <div className="text-xs text-gray-500 border-t pt-2">
          Tips: Velg publiseringsdato og legg inn NTB artikel-ID. ID finner du i URL-en eller i artikkelens metadata (f.eks. ID: 19371332). Datosøk gir mer presise resultater og unngår rate-limit.
        </div>
      </CardContent>
    </Card>
  );
};

export default NTBIntegration;